import type { ProjectAccess, UserKind, UserProject, UserStatus } from "@cloudserver/contracts";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "../../ui/toast.js";
import { Badge } from "../../ui/primitives.js";
import { problemText } from "../../ui/feedback.js";
import { ApiProblem } from "../../api/client.js";
import { StepUpCancelled } from "./step-up.js";

const KIND_TONE = {
  admin: "ok",
  project_owner: "neutral",
  guest: "neutral",
  mixed: "neutral",
  none: "warn",
} as const;

/** Administrator / Project owner / Guest / mixed / no access yet. */
export function KindBadge({ kind }: { kind: UserKind }) {
  const { t } = useTranslation();
  return <Badge tone={KIND_TONE[kind]}>{t(`users.kinds.${kind}`)}</Badge>;
}

const STATUS_TONE = { active: "ok", invited: "neutral", suspended: "danger" } as const;

export function StatusBadge({ status }: { status: UserStatus | "invited" }) {
  const { t } = useTranslation();
  return <Badge tone={STATUS_TONE[status]}>{t(`users.status.${status}`)}</Badge>;
}

export function AccessBadge({ access }: { access: ProjectAccess }) {
  const { t } = useTranslation();
  return <Badge tone={access === "guest" ? "neutral" : "ok"}>{t(`users.access.${access}`)}</Badge>;
}

/** "Стенд (владелец), Стенд Б (гость)". */
export function ProjectList({ projects }: { projects: UserProject[] }) {
  const { t } = useTranslation();
  if (projects.length === 0) return <span className="text-muted">—</span>;
  return (
    // Lines break between projects, never inside one («Стенд (Владелец)»).
    <span className="text-sm">
      {projects.map((p, index) => (
        <Fragment key={p.projectId}>
          {index > 0 && ", "}
          <span className="whitespace-nowrap">
            {t("users.projectWithAccess", {
              name: p.projectName,
              access: t(`users.access.${p.access}`),
            })}
          </span>
        </Fragment>
      ))}
    </span>
  );
}

/**
 * Runs a governance change and tells how it went; a closed step-up dialog
 * is no error. Returns whether the change went through.
 */
export function useChangeRunner() {
  const { t } = useTranslation();
  const toast = useToast();
  return async (change: () => Promise<unknown>, done: string): Promise<boolean> => {
    try {
      await change();
      toast({ tone: "ok", title: done });
      return true;
    } catch (error) {
      if (error instanceof StepUpCancelled) return false;
      toast({
        tone: "danger",
        title:
          error instanceof ApiProblem && error.problem
            ? problemText(t, error.problem)
            : t("common.unexpected"),
      });
      return false;
    }
  };
}
